import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { format } from 'date-fns';
import { dropTask, task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import PowerSelect from 'ember-power-select/components/power-select';
import { eq, not, or } from 'ember-truth-helpers';
import CloseButton from 'garaje/components/close-button';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import type { RequiredDocumentSignature } from 'garaje/components/flows/sections/required-documents/list';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type { ReUploadConfig } from 'garaje/models/custom-user-document-template';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import type StateService from 'garaje/services/state';

const CREATE_NEW_OPTION = { id: '__create_new__', title: '+ Create new document type' } as const;
type TemplateOption = CustomUserDocumentTemplateModel | typeof CREATE_NEW_OPTION;

type ReuploadPolicy = 'always' | 'never' | 'after_days' | 'on_expiration' | 'on_date_yearly';

interface FlowsSectionsRequiredDocumentsAddModalSignature {
  Args: {
    documentUploadPageId?: string;
    existingDocument?: RequiredDocumentSignature;
    existingDocumentIds?: string[];
    onClose: () => void;
    onSaved: (document: RequiredDocumentSignature) => void;
  };
}

export default class FlowsSectionsRequiredDocumentsAddModal extends Component<FlowsSectionsRequiredDocumentsAddModalSignature> {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;

  @tracked selectedOption: TemplateOption | null = null;
  @tracked newTitle = '';
  @tracked newDescription = '';
  @tracked parsingEnabled = false;
  @tracked reuploadPolicy: ReuploadPolicy = 'always';
  @tracked reuploadDays = '365';
  @tracked reuploadDate: Date | null = null;
  @tracked showDatePicker = false;
  @tracked templates: CustomUserDocumentTemplateModel[] = [];

  get isEditing(): boolean {
    return Boolean(this.args.existingDocument);
  }

  get isCreatingNew(): boolean {
    return this.selectedOption?.id === CREATE_NEW_OPTION.id;
  }

  get canSave(): boolean {
    if (!this.selectedOption) return false;
    if (this.reuploadPolicy === 'on_date_yearly' && !this.reuploadDate) return false;
    if (this.selectedOption.id === CREATE_NEW_OPTION.id) {
      return this.newTitle.trim().length > 0;
    }
    return true;
  }

  get dropdownOptions(): TemplateOption[] {
    const excludeIds = new Set(this.args.existingDocumentIds ?? []);
    const filtered = this.templates.filter((t) => !excludeIds.has(t.id));
    return [...filtered, CREATE_NEW_OPTION];
  }

  constructor(owner: unknown, args: FlowsSectionsRequiredDocumentsAddModalSignature['Args']) {
    super(owner, args);
    void this.loadTemplates.perform();
  }

  loadTemplates = task(async () => {
    if (!this.state.currentCompany?.id) return;
    try {
      const results = await this.store.query('custom-user-document-template', {});
      this.templates = results.slice();
      if (this.args.existingDocument) {
        const match = this.templates.find((t) => t.id === this.args.existingDocument!.id);
        if (match) {
          this.selectedOption = match;
          this.populateFromTemplate(match);
          this.restorePolicyFromDocument(this.args.existingDocument);
        }
      }
    } catch (e) {
      this.logger.warn('[AddModal] Failed to load document templates:', e as Error);
    }
  });

  populateFromTemplate(template: CustomUserDocumentTemplateModel): void {
    this.newTitle = template.title;
    this.newDescription = template.description ?? '';
    this.parsingEnabled = template.allowLlmParsing ?? template.parsingEnabled;
    if (template.reUploadConfig) {
      this.restorePolicyFromConfig(template.reUploadConfig);
    }
  }

  buildReUploadConfig(): ReUploadConfig {
    switch (this.reuploadPolicy) {
      case 'always':
        return { policy: 'always' };
      case 'never':
        return { policy: 'never' };
      case 'after_days':
        return { policy: 'after_days', days: parseInt(this.reuploadDays, 10) || 365 };
      case 'on_expiration':
        return { policy: 'on_expiration' };
      case 'on_date_yearly':
        return {
          policy: 'on_date_yearly',
          month: this.reuploadDate ? this.reuploadDate.getMonth() + 1 : undefined,
          day: this.reuploadDate ? this.reuploadDate.getDate() : undefined,
        };
      default:
        return { policy: 'never' };
    }
  }

  restorePolicyFromConfig(config: ReUploadConfig): void {
    this.reuploadPolicy = config.policy;
    if (config.policy === 'after_days' && config.days) {
      this.reuploadDays = String(config.days);
    }
    if (config.policy === 'on_date_yearly' && config.month && config.day) {
      const now = new Date();
      this.reuploadDate = new Date(now.getFullYear(), config.month - 1, config.day);
    }
  }

  saveDocument = dropTask(async () => {
    let template: CustomUserDocumentTemplateModel | null = null;
    let createdNewTemplate = false;

    try {
      if (this.isCreatingNew) {
        createdNewTemplate = true;
        template = this.store.createRecord('custom-user-document-template', {
          title: this.newTitle.trim(),
          description: this.newDescription.trim() || undefined,
          allowLlmParsing: this.parsingEnabled,
          reUploadConfig: this.buildReUploadConfig(),
          company: this.state.currentCompany,
        });
        await template.save();
      } else {
        template = this.selectedOption as CustomUserDocumentTemplateModel;
        // Persist any reupload policy changes the user made in the modal
        template.reUploadConfig = this.buildReUploadConfig();
        await template.save();
      }

      if (this.args.documentUploadPageId) {
        try {
          const templateSwapped =
            this.isEditing && this.args.existingDocument && this.args.existingDocument.id !== template.id;
          if (templateSwapped) {
            await this.store
              .adapterFor('document-upload-page')
              .removeFromRelationship(
                'document-upload-page',
                this.args.documentUploadPageId,
                'custom-user-document-templates',
                [{ type: 'custom-user-document-templates', id: this.args.existingDocument!.id }],
              );
          }
          if (!this.isEditing || templateSwapped) {
            await this.store
              .adapterFor('document-upload-page')
              .addToRelationship(
                'document-upload-page',
                this.args.documentUploadPageId,
                'custom-user-document-templates',
                [{ type: 'custom-user-document-templates', id: template.id }],
              );
          }
          // Reload the page so the Ember Data store reflects the backend relationship change.
          // This ensures the document list updates immediately after save and survives page refresh.
          await this.store.findRecord('document-upload-page', this.args.documentUploadPageId, {
            reload: true,
            include: 'custom-user-document-templates',
          });
        } catch (e) {
          this.logger.error('[AddModal] Failed to update document-upload-page relationship', e as Error);
          if (createdNewTemplate) await template.destroyRecord();
          this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
          return;
        }
      }

      this.args.onSaved({
        id: template.id,
        title: template.title,
        subtitle: template.description,
        policyName: this.policyDisplayName,
        reuploadPolicy: this.reuploadPolicy,
        reuploadDays: this.reuploadDays,
        reuploadDate: this.reuploadDate?.toISOString(),
        position: this.args.existingDocument?.position ?? 0,
      });

      this.flashMessages.showAndHideFlash('success', this.isEditing ? 'Document updated.' : 'Document added.');
      this.args.onClose();
    } catch (e) {
      this.logger.error('[AddModal] saveDocument failed', e as Error);
      if (createdNewTemplate && template) {
        try {
          template.rollbackAttributes();
          this.store.unloadRecord(template);
        } catch {
          // best-effort cleanup
        }
      } else if (template) {
        template.rollbackAttributes();
      }
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    }
  });

  selectOption = (option: TemplateOption): void => {
    this.selectedOption = option;
    // Reset to defaults first, then let populateFromTemplate override with template config
    this.reuploadPolicy = 'always';
    this.reuploadDays = '365';
    this.reuploadDate = null;
    if (option.id === CREATE_NEW_OPTION.id) {
      this.newTitle = '';
      this.newDescription = '';
      this.parsingEnabled = false;
    } else {
      this.populateFromTemplate(option as CustomUserDocumentTemplateModel);
    }
  };

  updateTitle = (value: string): void => {
    this.newTitle = value;
  };

  updateDescription = (value: string): void => {
    this.newDescription = value;
  };

  updateParsingEnabled = (checked: boolean): void => {
    this.parsingEnabled = checked;
  };

  updateReuploadPolicy = (evt: Event): void => {
    this.reuploadPolicy = (evt.target as HTMLInputElement).value as ReuploadPolicy;
  };

  updateReuploadDays = (value: string): void => {
    this.reuploadDays = value.replace(/\D/g, '');
  };

  get reuploadDateDisplay(): string {
    if (!this.reuploadDate) return 'Select a date…';
    return format(this.reuploadDate, 'MMM d, yyyy');
  }

  get policyDisplayName(): string {
    switch (this.reuploadPolicy) {
      case 'always':
        return 'Always';
      case 'never':
        return 'Never';
      case 'after_days':
        return `After ${this.reuploadDays}d`;
      case 'on_expiration':
        return 'On expiration';
      case 'on_date_yearly':
        return this.reuploadDate ? format(this.reuploadDate, 'MMM d') : 'Yearly date';
      default:
        return '';
    }
  }

  restorePolicyFromDocument(doc: RequiredDocumentSignature): void {
    if (doc.reuploadPolicy) {
      this.reuploadPolicy = doc.reuploadPolicy as ReuploadPolicy;
    }
    if (doc.reuploadDays) {
      this.reuploadDays = doc.reuploadDays;
    }
    if (doc.reuploadDate) {
      this.reuploadDate = new Date(doc.reuploadDate);
    }
  }

  toggleDatePicker = (): void => {
    this.showDatePicker = !this.showDatePicker;
  };

  selectReuploadDate = (date: Date): void => {
    this.reuploadDate = date;
    this.showDatePicker = false;
  };

  <template>
    <ModalDialog
      @clickOutsideToClose={{true}}
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @onClose={{@onClose}}
    >
      <FocusTrapper>
        <div class="relative" data-test-add-document-modal>

          {{! Header }}
          <div class="flex items-center justify-between px-6 py-4">
            {{! template-lint-disable attribute-indentation }}
            <h2 class="text-lg font-semibold m-0">{{if
                this.isEditing
                "Edit Required Document"
                "Add Required Document"
              }}</h2>
            <CloseButton
              class="!relative !top-auto !right-auto border-0 hover:bg-carbon-5 rounded"
              @onClick={{@onClose}}
            />
          </div>

          {{! Body }}
          <div class="px-6 pb-4 space-y-3">
            {{#if this.loadTemplates.isRunning}}
              <p class="text-sm text-carbon-50 text-center py-4">Loading document types…</p>
            {{else}}
              <div>
                <label class="block text-sm font-medium text-carbon-70 mb-2">
                  Document type
                  <span class="text-papaya-60">*</span>
                </label>
                <PowerSelect
                  @placeholder="Select a document type…"
                  @options={{this.dropdownOptions}}
                  @selected={{this.selectedOption}}
                  @onChange={{this.selectOption}}
                  @searchField="title"
                  @dropdownClass="in-modal"
                  data-test-document-type-select
                  as |option|
                >
                  {{option.title}}
                </PowerSelect>
              </div>

              {{#if this.selectedOption}}
                <div class="space-y-4 p-4 bg-carbon-5 border border-carbon-10 rounded-md" data-test-new-type-form>
                  <div>
                    <label for="new-doc-title" class="block text-sm font-medium text-carbon-70 mb-1">
                      Title
                      <span class="text-papaya-60">*</span>
                    </label>
                    <input
                      id="new-doc-title"
                      type="text"
                      class="input w-full"
                      placeholder="e.g. Vaccination Record"
                      value={{this.newTitle}}
                      {{on "input" (pick "target.value" this.updateTitle)}}
                      data-test-new-title
                    />
                  </div>

                  <div>
                    <label for="new-doc-description" class="block text-sm font-medium text-carbon-70 mb-1">
                      Description
                    </label>
                    <textarea
                      id="new-doc-description"
                      class="input w-full resize-none"
                      placeholder="e.g. Proof of vaccination required for site access"
                      rows="3"
                      {{on "input" (pick "target.value" this.updateDescription)}}
                      data-test-new-description
                    >{{this.newDescription}}</textarea>
                  </div>

                  <div class="flex items-start space-x-3 pt-1">
                    <input
                      type="checkbox"
                      id="parsing-enabled"
                      class="mt-0.5 shrink-0"
                      checked={{this.parsingEnabled}}
                      {{on "change" (pick "target.checked" this.updateParsingEnabled)}}
                      data-test-parsing-enabled
                    />
                    <label for="parsing-enabled" class="text-sm text-carbon-70 cursor-pointer select-none">
                      <strong class="text-carbon-90">Enable document information parsing</strong>
                      — extracts fields and expiration date
                    </label>
                  </div>
                </div>
              {{/if}}

              <div class="p-4 border border-carbon-10 rounded-md" data-test-reupload-policy>
                <h3 class="text-sm font-semibold text-carbon-70 uppercase tracking-wide m-0 mb-1">Expiration & re-upload
                  rules</h3>
                <div class="flex flex-col gap-3 mt-2">
                  <div class="flex items-center">
                    <label class="flex items-center gap-2">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="always"
                        checked={{eq this.reuploadPolicy "always"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="w-4 h-4 cursor-pointer accent-gem-50"
                        data-test-policy-always
                      />
                      <span class="-mb-0.5">Always require re-upload</span>
                    </label>
                  </div>

                  <div class="flex items-center">
                    <label class="flex items-center gap-2">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="never"
                        checked={{eq this.reuploadPolicy "never"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="w-4 h-4 cursor-pointer accent-gem-50"
                        data-test-policy-never
                      />
                      <span class="-mb-0.5">Never require re-upload</span>
                    </label>
                  </div>

                  <div class="flex items-center">
                    <label class="flex items-center gap-2">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="after_days"
                        checked={{eq this.reuploadPolicy "after_days"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="w-4 h-4 cursor-pointer accent-gem-50"
                        data-test-policy-after-days
                      />
                      <span class="-mb-0.5">Require re-upload after</span>
                      <input
                        type="number"
                        min="1"
                        max="9999"
                        step="1"
                        class="!m-0"
                        style="width:4rem;height:2rem"
                        disabled={{not (eq this.reuploadPolicy "after_days")}}
                        value={{this.reuploadDays}}
                        {{on "input" (pick "target.value" this.updateReuploadDays)}}
                        data-test-policy-days-input
                      />
                      <span class="-mb-0.5">days</span>
                    </label>
                  </div>

                  <div class="flex items-center">
                    <label class="flex items-center gap-2">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="on_expiration"
                        checked={{eq this.reuploadPolicy "on_expiration"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="w-4 h-4 cursor-pointer accent-gem-50"
                        data-test-policy-on-expiration
                      />
                      <span class="-mb-0.5">Require re-upload on document expiration</span>
                    </label>
                  </div>

                  <div class="flex items-center">
                    <label class="flex items-center gap-2">
                      <input
                        type="radio"
                        name="reupload-policy"
                        value="on_date_yearly"
                        checked={{eq this.reuploadPolicy "on_date_yearly"}}
                        {{on "change" this.updateReuploadPolicy}}
                        class="w-4 h-4 cursor-pointer accent-gem-50"
                        data-test-policy-on-date-yearly
                      />
                      <span class="-mb-0.5">Require re-upload yearly on a specific date</span>
                    </label>
                    {{#if (eq this.reuploadPolicy "on_date_yearly")}}
                      <div class="relative ml-2">
                        <button
                          type="button"
                          class="flex items-center gap-2 px-3 py-1 text-sm border rounded-md bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40
                            {{if this.showDatePicker 'border-gem-50' 'border-carbon-20'}}"
                          {{on "click" this.toggleDatePicker}}
                          data-test-policy-specific-date-trigger
                        >
                          <span
                            class="{{unless this.reuploadDate 'text-carbon-40'}}"
                          >{{this.reuploadDateDisplay}}</span>
                          <svg height="5" viewBox="0 0 8 5" width="8" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="m6.798 0-2.798 2.885-2.798-2.885-1.202 1.24 3.4 3.503a.83.83 0 0 0 1.2 0l3.4-3.503z"
                              fill="#3F4450"
                              fill-opacity=".5"
                            />
                          </svg>
                        </button>
                        {{#if this.showDatePicker}}
                          {{! template-lint-disable use-ui-card }}
                          <div
                            class="p-3 bg-white border border-carbon-20 rounded-lg"
                            style="position: absolute; bottom: calc(100% + 8px); right: 0; box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10); z-index: 214748364;"
                            data-test-policy-specific-date-input
                          >
                            <EnvoyOneUpCalendar
                              @selectedDate={{this.reuploadDate}}
                              @onSelectDate={{this.selectReuploadDate}}
                            />
                          </div>
                        {{/if}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              </div>
            {{/if}}
          </div>

          {{! Footer }}
          <footer class="px-6 py-4">
            <ButtonGroup class="flex justify-end" @reverse={{true}}>
              <button
                type="button"
                class="btn btn--brand"
                disabled={{or (not this.canSave) this.saveDocument.isRunning}}
                {{on "click" (perform this.saveDocument)}}
                data-test-save
              >
                {{if this.saveDocument.isRunning "Saving…" (if this.isEditing "Save changes" "Add document")}}
              </button>
              <button
                type="button"
                class="btn btn--outlineFill"
                disabled={{this.saveDocument.isRunning}}
                {{on "click" @onClose}}
                data-test-cancel
              >
                Cancel
              </button>
            </ButtonGroup>
          </footer>

        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
